import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Modal,
  Row,
  Select,
  Table,
  Tabs,
} from "antd";
import { BarsOutlined } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import RequestHandler from "../../util/RequestHandler";
import { ColumnHelper } from "../../util/TableHelper";

const { TabPane } = Tabs;

const CREDIT_TRANSACTION_PAGE_SIZE = 10;
const DEFAULT_FILTER_DATE = "1970-01-01T00:00:00";
const TRANSACTION_SYNC_TTL_MS = 60 * 1000;

const TAB_KEYS = {
  CURRENT: "current",
  PROVISION: "provision",
  INSTALLMENT: "installment",
};

const EMPTY_STATE_STYLE = {
  minHeight: 260,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "32px 16px",
};

const getField = (source, fieldNames, fallback = undefined) => {
  for (const fieldName of fieldNames) {
    if (source?.[fieldName] !== undefined && source?.[fieldName] !== null) {
      return source[fieldName];
    }
  }

  return fallback;
};

const getFirstValue = (sources, fieldNames, fallback = "") => {
  for (const source of sources) {
    const value = getField(source, fieldNames);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

const normalizePreviewResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.value)) return response.value;

  return [];
};

const normalizeTransactionResponse = (response) => {
  const rows = Array.isArray(response)
    ? response
    : Array.isArray(response?.value)
      ? response.value
      : Array.isArray(response?.data)
        ? response.data
        : [];

  const total =
    response?.count ??
    response?.Count ??
    response?.totalCount ??
    response?.TotalCount ??
    rows.length;

  return {
    data: rows,
    total,
  };
};

const formatDetailDate = (value) => {
  if (!value) return "";

  const parsedDate = moment(value);
  if (!parsedDate.isValid()) return "";

  const year = parsedDate.year();
  const month = parsedDate.month();
  const day = parsedDate.date();

  if ((year === 1 && month === 0 && day === 1) || (year === 1970 && month === 0 && day === 1)) {
    return "";
  }

  return parsedDate.format("DD.MM.YYYY");
};

const formatAmountWithCurrency = (amount, currency) => {
  if (amount === "" || amount === null || amount === undefined) {
    return "";
  }

  const formattedAmount = ColumnHelper.renderDecimal(amount);

  return currency ? `${formattedAmount} ${currency}` : formattedAmount;
};

const isRealCreditCard = (card) =>
  card?.cardTypeCode === "K" && card?.cardSubTypeCode !== "S";

const buildInstallmentMonthOptions = (periodEndDateValue) => {
  const parsedDate = moment(periodEndDateValue);
  if (!parsedDate.isValid()) {
    return [];
  }

  const firstFutureMonth = parsedDate.clone().add(1, "month").startOf("month");

  return Array.from({ length: 18 }, (_, index) => {
    const periodDate = firstFutureMonth.clone().add(index, "month");
    const periodValue = index + 1;
    const label = periodDate.format("MM/YYYY");

    return {
      month: periodDate.month() + 1,
      year: periodDate.year(),
      label,
      displayDate: label,
      periodValue,
      key: `${periodDate.year()}-${periodDate.month() + 1}-${periodValue}`,
    };
  });
};

const buildCurrentPayload = (tenantCardId, statementCurrency, page) => ({
  tenantCardId,
  statementCurrency,
  currentPeriodOnly: true,
  startDate: DEFAULT_FILTER_DATE,
  endDate: DEFAULT_FILTER_DATE,
  debitCreditIndicator: "",
  firstData: 0,
  count: CREDIT_TRANSACTION_PAGE_SIZE,
  page,
  order: "",
});

const buildInstallmentPayload = (
  tenantCardId,
  statementCurrency,
  selectedMonth,
  page,
) => ({
  tenantCardId,
  statementCurrency,
  periodEndMonth: selectedMonth.month,
  periodEndYear: selectedMonth.year,
  currentPeriodOnly: false,
  startDate: DEFAULT_FILTER_DATE,
  endDate: DEFAULT_FILTER_DATE,
  debitCreditIndicator: "",
  firstData: 0,
  count: CREDIT_TRANSACTION_PAGE_SIZE,
  page,
  order: "",
});

const BkmCardTransactions = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedCard = location.state?.selectedCard;
  const statementCurrency = location.state?.statementCurrency;

  const routeStateRef = useRef(location.state || {});
  const syncStateRef = useRef(location.state?.transactionSyncState || {});

  const [activeTab, setActiveTab] = useState(TAB_KEYS.CURRENT);
  const [currentData, setCurrentData] = useState({
    data: [],
    total: 0,
    loading: false,
  });
  const [provisionData, setProvisionData] = useState({
    data: [],
    loading: false,
  });
  const [installmentData, setInstallmentData] = useState({
    data: [],
    total: 0,
    loading: false,
  });
  const [selectedPage, setSelectedPage] = useState(1);
  const [provisionPage, setProvisionPage] = useState(1);
  const [installmentPage, setInstallmentPage] = useState(1);
  const [installmentMonthOptions, setInstallmentMonthOptions] = useState([]);
  const [selectedInstallmentMonth, setSelectedInstallmentMonth] = useState(
    undefined,
  );
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const showInstallmentTab = isRealCreditCard(selectedCard);

  const activeInstallmentMonth = useMemo(
    () =>
      installmentMonthOptions.find(
        (item) => item.key === selectedInstallmentMonth,
      ),
    [installmentMonthOptions, selectedInstallmentMonth],
  );

  const pagedProvisionData = useMemo(() => {
    const startIndex = (provisionPage - 1) * CREDIT_TRANSACTION_PAGE_SIZE;
    const endIndex = startIndex + CREDIT_TRANSACTION_PAGE_SIZE;

    return provisionData.data.slice(startIndex, endIndex);
  }, [provisionData.data, provisionPage]);

  const persistRouteState = (overrides = {}) => {
    routeStateRef.current = {
      ...routeStateRef.current,
      ...overrides,
      selectedCard,
      statementCurrency,
    };

    history.replace({
      pathname: location.pathname,
      state: routeStateRef.current,
    });
  };

  const getInstallmentSyncKey = (selectedMonth) =>
    `${selectedCard.id}|${statementCurrency}|${selectedMonth.periodValue}-${selectedMonth.month}-${selectedMonth.year}`;

  const getInstallmentSyncStorageKey = (selectedMonth) =>
    `bkm-installment-sync:${getInstallmentSyncKey(selectedMonth)}`;

  const isInstallmentSyncFresh = (selectedMonth) => {
    if (!selectedMonth || !selectedCard?.id || !statementCurrency) {
      return false;
    }

    const syncKey = getInstallmentSyncKey(selectedMonth);
    const storageKey = getInstallmentSyncStorageKey(selectedMonth);
    const memoryValue = syncStateRef.current?.[syncKey];
    const storedValue = window.sessionStorage.getItem(storageKey);
    const lastSyncedAt = Number(memoryValue || storedValue || 0);

    return (
      Number.isFinite(lastSyncedAt) &&
      lastSyncedAt > 0 &&
      Date.now() - lastSyncedAt < TRANSACTION_SYNC_TTL_MS
    );
  };

  const markInstallmentSync = (selectedMonth) => {
    const syncKey = getInstallmentSyncKey(selectedMonth);
    const storageKey = getInstallmentSyncStorageKey(selectedMonth);
    const syncedAt = Date.now();

    syncStateRef.current = {
      ...syncStateRef.current,
      [syncKey]: syncedAt,
    };

    window.sessionStorage.setItem(storageKey, String(syncedAt));
    persistRouteState({
      transactionSyncState: syncStateRef.current,
    });
  };

  useEffect(() => {
    if (!selectedCard?.id || !statementCurrency) {
      return;
    }

    routeStateRef.current = location.state || {};
    syncStateRef.current = routeStateRef.current.transactionSyncState || {};

    const restoredActiveTab = routeStateRef.current.activeTab;
    const restoredOptions = Array.isArray(routeStateRef.current.installmentMonthOptions)
      ? routeStateRef.current.installmentMonthOptions
      : [];
    const restoredInstallmentKey = routeStateRef.current.selectedInstallmentMonth;
    const restoredInstallmentPage =
      Number(routeStateRef.current.installmentPage) || 1;

    const initialTab =
      restoredActiveTab === TAB_KEYS.PROVISION ||
      restoredActiveTab === TAB_KEYS.CURRENT ||
      (restoredActiveTab === TAB_KEYS.INSTALLMENT && showInstallmentTab)
        ? restoredActiveTab
        : TAB_KEYS.CURRENT;

    setActiveTab(initialTab);
    setSelectedPage(1);
    setProvisionPage(1);
    setInstallmentPage(restoredInstallmentPage);
    setInstallmentMonthOptions(restoredOptions);
    setSelectedInstallmentMonth(restoredInstallmentKey);
    setSelectedTransaction(null);
    setCurrentData({
      data: [],
      total: 0,
      loading: false,
    });
    setProvisionData({
      data: [],
      loading: false,
    });
    setInstallmentData({
      data: [],
      total: 0,
      loading: false,
    });

    persistRouteState({
      activeTab: initialTab,
      selectedInstallmentMonth: restoredInstallmentKey,
      installmentMonthOptions: restoredOptions,
      installmentPage: restoredInstallmentPage,
      transactionSyncState: syncStateRef.current,
    });

    if (initialTab === TAB_KEYS.PROVISION) {
      loadProvisionTransactions();
      return;
    }

    if (initialTab === TAB_KEYS.INSTALLMENT && showInstallmentTab) {
      if (restoredOptions.length > 0) {
        const restoredMonth =
          restoredOptions.find((item) => item.key === restoredInstallmentKey) ||
          restoredOptions[0];

        setSelectedInstallmentMonth(restoredMonth.key);
        setInstallmentPage(restoredInstallmentPage);
        persistRouteState({
          selectedInstallmentMonth: restoredMonth.key,
          installmentMonthOptions: restoredOptions,
          installmentPage: restoredInstallmentPage,
          activeTab: TAB_KEYS.INSTALLMENT,
        });
        loadInstallmentTransactions(restoredMonth, restoredInstallmentPage);
      } else {
        loadInstallmentMonthOptions(
          restoredInstallmentKey,
          restoredInstallmentPage,
        );
      }
      return;
    }

    loadCurrentTransactions(1, true);
  }, [selectedCard?.id, statementCurrency]);

  const loadCurrentTransactions = async (page = 1, shouldSync = false) => {
    if (!selectedCard?.id || !statementCurrency) return;

    setCurrentData((prevState) => ({
      ...prevState,
      loading: true,
    }));

    if (shouldSync) {
      const syncResponse = await RequestHandler.get(
        `/BkmCardSync/SyncCardTransactions/${selectedCard.id}?periodValue=0&statementCurrency=${statementCurrency}`,
      );

      if (syncResponse === undefined) {
        setCurrentData((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return;
      }
    }

    const response = await RequestHandler.post(
      `/CardTransaction/GetListByFilter`,
      buildCurrentPayload(selectedCard.id, statementCurrency, page),
      false,
    );

    const normalized = normalizeTransactionResponse(response);

    setCurrentData({
      data: normalized.data,
      total: normalized.total,
      loading: false,
    });
    setSelectedPage(page);
    persistRouteState({
      activeTab: TAB_KEYS.CURRENT,
    });
  };

  const loadProvisionTransactions = async () => {
    if (!selectedCard?.id || !statementCurrency) return;

    setProvisionData((prevState) => ({
      ...prevState,
      loading: true,
    }));

    const response = await RequestHandler.get(
      `/BkmCardSync/GetCardTransactionsPreview/${selectedCard.id}?periodValue=99&statementCurrency=${statementCurrency}`,
    );

    const normalized = normalizePreviewResponse(response);

    setProvisionData({
      data: normalized,
      loading: false,
    });
    setProvisionPage(1);
    persistRouteState({
      activeTab: TAB_KEYS.PROVISION,
    });
  };

  const loadInstallmentMonthOptions = async (
    preferredPeriodKey,
    preferredPage = 1,
  ) => {
    if (!selectedCard?.id || !statementCurrency || !showInstallmentTab) return;

    setInstallmentData((prevState) => ({
      ...prevState,
      loading: true,
    }));

    const currentPeriodResponse = await RequestHandler.get(
      `/CardTransactionPeriod/GetCurrentPeriod?tenantCardId=${selectedCard.id}&statementCurrency=${statementCurrency}`,
    );

    if (currentPeriodResponse === undefined) {
      setInstallmentData((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return;
    }

    const periodEndDate = getFirstValue(
      [currentPeriodResponse, currentPeriodResponse?.data],
      ["periodEndDate", "PeriodEndDate"],
      "",
    );

    const options = buildInstallmentMonthOptions(periodEndDate);
    setInstallmentMonthOptions(options);

    if (options.length === 0) {
      setSelectedInstallmentMonth(undefined);
      setInstallmentData({
        data: [],
        total: 0,
        loading: false,
      });
      persistRouteState({
        activeTab: TAB_KEYS.INSTALLMENT,
        installmentMonthOptions: [],
        selectedInstallmentMonth: undefined,
        installmentPage: 1,
      });
      return;
    }

    const nextSelectedMonth =
      options.find((item) => item.key === preferredPeriodKey) || options[0];

    setSelectedInstallmentMonth(nextSelectedMonth.key);
    setInstallmentPage(preferredPage);
    persistRouteState({
      activeTab: TAB_KEYS.INSTALLMENT,
      installmentMonthOptions: options,
      selectedInstallmentMonth: nextSelectedMonth.key,
      installmentPage: preferredPage,
    });

    await loadInstallmentTransactions(nextSelectedMonth, preferredPage);
  };

  const loadInstallmentTransactions = async (selectedMonth, page = 1) => {
    if (
      !selectedCard?.id ||
      !statementCurrency ||
      !selectedMonth ||
      !showInstallmentTab
    ) {
      return;
    }

    setInstallmentData((prevState) => ({
      ...prevState,
      loading: true,
    }));

    const needsSync = !isInstallmentSyncFresh(selectedMonth);

    if (needsSync) {
      const syncResponse = await RequestHandler.get(
        `/BkmCardSync/SyncCardTransactions/${selectedCard.id}?periodValue=${selectedMonth.periodValue}&statementCurrency=${statementCurrency}`,
      );

      if (syncResponse === undefined) {
        setInstallmentData((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return;
      }

      markInstallmentSync(selectedMonth);
    }

    const response = await RequestHandler.post(
      `/CardTransaction/GetListByFilter`,
      buildInstallmentPayload(
        selectedCard.id,
        statementCurrency,
        selectedMonth,
        page,
      ),
      false,
    );

    const normalized = normalizeTransactionResponse(response);

    setInstallmentData({
      data: normalized.data,
      total: normalized.total,
      loading: false,
    });
    setInstallmentPage(page);
    setSelectedInstallmentMonth(selectedMonth.key);
    persistRouteState({
      activeTab: TAB_KEYS.INSTALLMENT,
      selectedInstallmentMonth: selectedMonth.key,
      installmentMonthOptions,
      installmentPage: page,
      transactionSyncState: syncStateRef.current,
    });
  };

  const handleTabChange = async (nextTab) => {
    setActiveTab(nextTab);
    persistRouteState({
      activeTab: nextTab,
      selectedInstallmentMonth,
      installmentMonthOptions,
      installmentPage,
      transactionSyncState: syncStateRef.current,
    });

    if (nextTab === TAB_KEYS.CURRENT && currentData.data.length === 0) {
      await loadCurrentTransactions(1, true);
      return;
    }

    if (nextTab === TAB_KEYS.PROVISION && provisionData.data.length === 0) {
      await loadProvisionTransactions();
      return;
    }

    if (nextTab === TAB_KEYS.INSTALLMENT && showInstallmentTab) {
      if (installmentMonthOptions.length > 0) {
        const nextMonth =
          installmentMonthOptions.find(
            (item) => item.key === selectedInstallmentMonth,
          ) || installmentMonthOptions[0];

        if (installmentData.data.length === 0) {
          await loadInstallmentTransactions(nextMonth, installmentPage || 1);
        }
        return;
      }

      await loadInstallmentMonthOptions(selectedInstallmentMonth, installmentPage || 1);
    }
  };

  const handleInstallmentMonthChange = async (periodKey) => {
    const nextMonth = installmentMonthOptions.find(
      (item) => item.key === periodKey,
    );
    if (!nextMonth) return;

    setInstallmentPage(1);
    setSelectedInstallmentMonth(periodKey);
    persistRouteState({
      activeTab: TAB_KEYS.INSTALLMENT,
      selectedInstallmentMonth: periodKey,
      installmentMonthOptions,
      installmentPage: 1,
      transactionSyncState: syncStateRef.current,
    });
    await loadInstallmentTransactions(nextMonth, 1);
  };

  const getTransactionAmountText = (row) =>
    formatAmountWithCurrency(
      getFirstValue(
        [row],
        ["transactionAmount", "TransactionAmount", "amount", "Amount"],
        "",
      ),
      getFirstValue(
        [row],
        [
          "transactionAmountCurrency",
          "TransactionAmountCurrency",
          "currencyCode",
          "CurrencyCode",
        ],
        "",
      ),
    );

  const isDebitTransaction = (row) =>
    getFirstValue(
      [row],
      ["debitCreditIndicator", "DebitCreditIndicator"],
      "",
    ) === "B" ||
    Number(
      getFirstValue(
        [row],
        ["transactionProcessType", "TransactionProcessType"],
        "",
      ),
    ) === 2;

  const transactionColumns = [
    {
      title: "İşlem Tarihi",
      dataIndex: "transactionDate",
      key: "transactionDate",
      render: (_, row) =>
        formatDetailDate(
          getFirstValue([row], ["transactionDate", "TransactionDate"], ""),
        ),
    },
    {
      title: "Açıklama",
      dataIndex: "description",
      key: "description",
      render: (_, row) =>
        getFirstValue(
          [row],
          [
            "transactionDescription",
            "TransactionDescription",
            "description",
            "Description",
          ],
          "",
        ),
    },
    {
      title: "İşlem Tutarı",
      dataIndex: "transactionAmount",
      key: "transactionAmount",
      render: (_, row) => (
        <span
          style={isDebitTransaction(row) ? { color: "#ff4d4f", fontWeight: 600 } : {}}
        >
          {getTransactionAmountText(row)}
        </span>
      ),
    },
    {
      title: "",
      key: "detail",
      align: "right",
      render: (_, row) => (
        <Button
          type="link"
          onClick={(event) => {
            event.stopPropagation();
            setSelectedTransaction(row);
          }}
        >
          Detay
        </Button>
      ),
    },
  ];

  const renderTransactionTable = ({
    dataSource,
    loading,
    pagination,
    showMonthSelector = false,
  }) => (
    <>
      {showMonthSelector && installmentMonthOptions.length > 0 && (
        <div style={{ padding: "0 16px 12px" }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <label style={{ display: "block", marginBottom: 6 }}>
                Taksit Dönemi
              </label>
              <Select
                value={selectedInstallmentMonth}
                onChange={handleInstallmentMonthChange}
                style={{ width: "100%" }}
              >
                {installmentMonthOptions.map((item) => (
                  <Select.Option key={item.key} value={item.key}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </Row>
        </div>
      )}

      {loading || dataSource.length > 0 ? (
        <Table
          size="small"
          columns={transactionColumns}
          dataSource={dataSource}
          loading={loading}
          rowKey={(row, index) => row?.id || row?.Id || index}
          pagination={pagination}
          onRow={(record) => ({
            onClick: () => setSelectedTransaction(record),
            style: { cursor: "pointer" },
          })}
        />
      ) : (
        <div style={EMPTY_STATE_STYLE}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Veri Yok"
          />
        </div>
      )}
    </>
  );

  if (!selectedCard?.id || !statementCurrency) {
    return (
      <>
        <Row>
          <Col span={24}>
            <p style={{ fontSize: "120%", marginTop: -5, marginBottom: 12 }}>
              <BarsOutlined /> KART HAREKETLERİ
            </p>
          </Col>
        </Row>

        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Kart seçimi bulunamadı"
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <Row>
        <Col span={24}>
          <p style={{ fontSize: "120%", marginTop: -5, marginBottom: 12 }}>
            <BarsOutlined /> KART HAREKETLERİ
          </p>
        </Col>
      </Row>

      <Card bodyStyle={{ padding: 0 }}>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Güncel Dönem" key={TAB_KEYS.CURRENT}>
            {renderTransactionTable({
              dataSource: currentData.data,
              loading: currentData.loading,
              pagination: {
                current: selectedPage,
                pageSize: CREDIT_TRANSACTION_PAGE_SIZE,
                total: currentData.total,
                showSizeChanger: false,
                onChange: (page) => loadCurrentTransactions(page, false),
              },
            })}
          </TabPane>

          <TabPane tab="Bekleyen Provizyon" key={TAB_KEYS.PROVISION}>
            {renderTransactionTable({
              dataSource: pagedProvisionData,
              loading: provisionData.loading,
              pagination: {
                current: provisionPage,
                pageSize: CREDIT_TRANSACTION_PAGE_SIZE,
                total: provisionData.data.length,
                showSizeChanger: false,
                onChange: (page) => setProvisionPage(page),
              },
            })}
          </TabPane>

          {showInstallmentTab && (
            <TabPane tab="Bekleyen Taksitler" key={TAB_KEYS.INSTALLMENT}>
              {renderTransactionTable({
                dataSource: installmentData.data,
                loading: installmentData.loading,
                pagination: {
                  current: installmentPage,
                  pageSize: CREDIT_TRANSACTION_PAGE_SIZE,
                  total: installmentData.total,
                  showSizeChanger: false,
                  onChange: (page) =>
                    loadInstallmentTransactions(
                      activeInstallmentMonth,
                      page,
                    ),
                },
                showMonthSelector: true,
              })}
            </TabPane>
          )}
        </Tabs>
      </Card>

      <Modal
        visible={!!selectedTransaction}
        footer={null}
        title="İşlem Detayı"
        onCancel={() => setSelectedTransaction(null)}
      >
        {selectedTransaction && (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="İşlem Tarihi">
              {formatDetailDate(
                getFirstValue(
                  [selectedTransaction],
                  ["transactionDate", "TransactionDate"],
                  "",
                ),
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Kanal">
              Mobil Bankacılık
            </Descriptions.Item>
            <Descriptions.Item label="İşlem Tutarı">
              {getTransactionAmountText(selectedTransaction)}
            </Descriptions.Item>
            <Descriptions.Item label="Açıklama">
              {getFirstValue(
                [selectedTransaction],
                [
                  "transactionDescription",
                  "TransactionDescription",
                  "description",
                  "Description",
                ],
                "",
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default BkmCardTransactions;
